import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { CheckIcon } from "@heroicons/react/20/solid";
import useResetOnReturn from "../../hooks/useResetOnReturn";
import {
  cachedActiveOrganisation,
  cacheActiveOrganisation,
  fetchMyOrganisations,
  orgErrorMessage,
  roleLabel,
  startSession,
  switchOrganisation,
} from "../../utils/organisationApi";

// Lists the person's organisations inside the profile menu. The list comes from GET /me.
const OrganisationSwitcher = ({ session }) => {
  const theme = useTheme();
  const fromProfile = Array.isArray(session.organisations);
  const [organisations, setOrganisations] = useState(fromProfile ? session.organisations : []);
  const [loading, setLoading] = useState(!fromProfile);
  const [switchingId, setSwitchingId] = useState("");
  // Back from an organisation that was just switched to: nothing should stay on "switching"
  useResetOnReturn(() => setSwitchingId(""));
  const [error, setError] = useState("");
  const activeId = session.organisationId;

  useEffect(() => {
    if (fromProfile) {
      setOrganisations(session.organisations);
      setLoading(false);
      const active = session.organisations.find((org) => org.id === activeId);
      if (active) cacheActiveOrganisation({ id: active.id, name: active.name });
      return undefined;
    }
    let cancelled = false;
    fetchMyOrganisations()
      .then(({ organisations: list }) => {
        if (cancelled) return;
        setOrganisations(list);
        const active = list.find((org) => org.id === activeId);
        if (active) cacheActiveOrganisation({ id: active.id, name: active.name });
      })
      .catch((err) => {
        if (!cancelled) setError(orgErrorMessage(err, "Could not load organisations."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeId, fromProfile, session.organisations]);

  const handleSwitch = async (org) => {
    if (org.id === activeId || switchingId) return;
    setSwitchingId(org.id);
    setError("");
    try {
      const token = await switchOrganisation(org.id);
      if (!token) throw new Error("No session returned.");
      startSession(token, "/");
    } catch (err) {
      setError(orgErrorMessage(err, `Could not switch to ${org.name}.`));
      setSwitchingId("");
    }
  };

  if (loading) {
    const cached = cachedActiveOrganisation();
    return (
      <div className="px-4 py-3 border-b border-gray-700 text-sm text-accent-gray-light">
        {cached?.name || "Loading organisations..."}
      </div>
    );
  }

  return (
    <div className="border-b border-gray-700 org-switcher-container">
      <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-accent-gray-light">
        {organisations.length > 1 ? "Switch organisation" : "Organisation"}
      </p>
      <ul className="max-h-56 overflow-y-auto pb-1" aria-label="Your organisations">
        {organisations.map((org) => {
          const active = org.id === activeId;
          return (
            <li key={org.id}>
              <button
                onClick={() => handleSwitch(org)}
                aria-current={active ? "true" : undefined}
                disabled={Boolean(switchingId)}
                className={`flex items-center gap-3 w-full px-4 py-2 text-left ${active ? "cursor-default" : "hover:!bg-accent-blue hover:text-white"}`}
              >
                <span
                  className="w-8 h-8 shrink-0 flex items-center justify-center rounded text-white text-sm font-bold"
                  style={{ backgroundColor: active ? theme.palette.info.main : theme.palette.secondary.v6 }}
                  aria-hidden="true"
                >
                  {org.name?.[0]?.toUpperCase() || "O"}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate font-medium">{org.name}</span>
                  <span className="block text-xs opacity-80">
                    {switchingId === org.id ? "Switching..." : roleLabel(org.role)}
                  </span>
                </span>
                {active && <CheckIcon className="h-4 w-4 shrink-0" style={{ color: theme.palette.info.main }} aria-label="Current" />}
              </button>
            </li>
          );
        })}
      </ul>
      {error && <p className="px-4 pb-2 text-sm text-accent-red">{error}</p>}
    </div>
  );
};

export default OrganisationSwitcher;
